import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Container, Form } from 'react-bootstrap';
import styles from './ClinicMaterialsProtocols.module.scss';
import DashboardTableCard from '../components/DashboardTableCard';
import { Breadcrumb, Row, Col } from 'react-bootstrap';
import Loader from '../components/Loader';
import ModalForm from '../components/ModalForm';
import Button from '../components/Button';
import SubmitButton from '../components/SubmitButton';
import { alertModal } from '../utils/modalController';
import { Link } from 'react-router-dom';
import { fetchAuth } from '../utils/fetchAuth';

const EditProtocolForm = lazy(() => import('../components/EditProtocolForm'));
const EditContractModal = lazy(() => import('../components/EditContractModal'));
const apiUrl = process.env.REACT_APP_API_URL || '';

const ClinicMaterialsProtocols: React.FC = () => {

    const [protocolos, setProtocolos] = useState<Protocolo[]>([]);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [showProtocolModal, setShowProtocolModal] = useState(false);
    const [selectedProtocol, setSelectedProtocol] = useState<Protocolo | null>(null);
    const [showContractModal, setShowContractModal] = useState(false);
    const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        Promise.all([
            fetchAuth(apiUrl + '/consultorio/protocolos'),
            fetchAuth(apiUrl + '/consultorio/contracts'),
        ])
            .then(async ([protocolosRes, contratosRes]) => {
                if (!protocolosRes.ok) {
                    const data = await protocolosRes.json();
                    return Promise.reject(data.message || 'Erro ao carregar protocolos');
                }
                if (!contratosRes.ok) {
                    const data = await contratosRes.json();
                    return Promise.reject(data.message || 'Erro ao carregar contratos');
                }

                const protocolosJson = await protocolosRes.json();
                const contratosJson = await contratosRes.json();
                setProtocolos(protocolosJson);
                setContracts(contratosJson);
            })
            .catch((err) => {
                console.error('Erro ao buscar dados:', err);
                alertModal('Erro ao buscar dados: ' + err);
                setProtocolos([]);
                setContracts([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [saving, deleting, showContractModal]); // Dependências para recarregar os dados quando o modal for fechado

    if (loading) return <Loader />;

    return (
        <>
            <Container className="center">
                <h2 className={styles["title"]}>Consultório</h2>
                <Row className={styles["row-breadcrumb"]}>
                    <Col>
                        <Breadcrumb className={styles.breadcrumb}>
                            <Breadcrumb.Item linkAs={Link} linkProps={{to:"/consultorio"}}>Consultório</Breadcrumb.Item>
                            <Breadcrumb.Item active>Protocolos de Atendimento</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <DashboardTableCard
                    welcomeMessage={`Protocolos de Atendimento`}
                    tableData={{
                        headings: ['Nome', 'Qnt. consultas', 'Validade', 'Valor'],
                        data: [
                            [<a href='#' onClick={(e) => {
                                e.preventDefault();
                                setSelectedProtocol(null);
                                setShowProtocolModal(true);
                                setSelectedContract(null); // Limpa o contrato selecionado ao criar um novo protocolo
                            }} className={styles["link"]}>Novo protocolo</a>],
                            ...protocolos.map((protocolo) => [
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedProtocol(protocolo);
                                    setSelectedContract(contracts.find(p => p.id === protocolo.contract?.id) || null); // Seleciona o contrato associado ao protocolo
                                    setShowProtocolModal(true);
                                }}
                                    className={styles["link"]}
                                    key={protocolo.id}>
                                    {protocolo.title}
                                </a>,
                                protocolo.number_of_sessions ? protocolo.number_of_sessions : 'Ilimitado',
                                protocolo.duration ? `${protocolo.duration} dias` : 'Vitalício',
                                (protocolo.valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            ])
                        ]

                    }}
                />
            </Container>
            <ModalForm
                show={showProtocolModal}
                onHide={() => setShowProtocolModal(false)}
                onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const formDataJson = Object.fromEntries(formData.entries());
                    setSaving(true);
                    fetchAuth(apiUrl + "/consultorio/protocolos" + (selectedProtocol?.id ? `/${selectedProtocol.id}` : ""), {
                        method: selectedProtocol?.id ? "PUT" : "POST",
                        body: JSON.stringify({
                            ...formDataJson,
                        }),
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    })
                        .then(async (response) => {
                            if (!response.ok) {
                                const data = await response.json();
                                return Promise.reject(data.message || 'Erro ao salvar protocolo');
                            }
                            return response.json();
                        })
                        .then((response_json) => {
                            setShowProtocolModal(false);
                            setSelectedProtocol(null);
                            setSelectedContract(null); // Limpa o contrato selecionado após salvar o protocolo
                        })
                        .catch((error) => {
                            console.error("Erro ao salvar protocolo:", error);
                            alertModal("Erro ao salvar protocolo: " + error.message);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }
                }
                title="Protocolo de Atendimento"
                footerButtons={[
                    <Button
                        label="Cancelar"
                        className={styles['buttons']}
                        onClick={() => setShowProtocolModal(false)}
                        variant="secondary"
                        disabled={saving}
                    />,
                    <Button
                        className={[styles['buttons'], selectedProtocol ? 'd-none' : 'd-none'].join(' ')}
                        onClick={() => {
                            if (selectedProtocol) {
                                setDeleting(true);
                                fetchAuth(apiUrl + "/consultorio/protocolos/" + selectedProtocol.id, {
                                    method: "DELETE",
                                })
                                    .then(async (response) => {
                                        if (!response.ok) {
                                            const data = await response.json();
                                            return Promise.reject(data.message || 'Erro ao remover protocolo');
                                        }
                                        return response.json();
                                    })
                                    .then((response_json) => {
                                        setShowProtocolModal(false);
                                    })
                                    .catch((error) => {
                                        console.error("Erro ao remover usuário:", error);
                                        alertModal("Erro ao remover usuário: " + error.message);
                                    })
                                    .finally(() => {
                                        setDeleting(false);
                                    });
                            }
                        }}
                        variant="secondary"
                        disabled={saving || !selectedProtocol || deleting}
                    >
                        {deleting ? "Removendo..." : "Remover"}
                    </Button>,
                    <SubmitButton
                        className={styles['buttons']}
                        disabled={saving || deleting}
                    >
                        {saving ? "Salvando..." : "Salvar"}
                    </SubmitButton>,
                ]}
            >
                <Form.Control type="hidden" name="id" defaultValue={selectedProtocol?.id || ''} />
                <Suspense fallback={<Loader />}>
                    <EditProtocolForm
                        protocol={selectedProtocol || undefined}
                        contracts={contracts}
                        onCreateContract={() => {
                            setSelectedContract(null);
                            setShowProtocolModal(false);
                            setShowContractModal(true);
                        }}
                        onEditContract={(contractId: number) => {
                            setSelectedContract(contracts.find(p => p.id === contractId) || null);
                            setShowProtocolModal(false);
                            setShowContractModal(true);
                        }}
                        onRemoveContract={(contractId: number) => {
                            setSelectedContract(contracts.find(p => p.id === contractId) || null);
                            setShowProtocolModal(false);
                            setShowContractModal(true);
                        }}
                    />
                </Suspense>
            </ModalForm>
            <Suspense fallback={<Loader />}>
                <EditContractModal
                    showModal={showContractModal}
                    contract={selectedContract || undefined}
                    onHide={() => setShowContractModal(false)}
                    onDataLoaded={(contract_id: number) => {
                        setSelectedContract(contracts.find(p => p.id === contract_id) || null);
                        setShowContractModal(false);
                        setSelectedProtocol({
                            ...selectedProtocol,
                            contract_id: contract_id,
                        } as Protocolo);
                        setShowProtocolModal(true);
                    }}

                />
            </Suspense>
        </>
    );
};

export default ClinicMaterialsProtocols;
